import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'next/router';
import Head from 'next/head';
import { toQuerystring } from '../../routing/routes';
import * as field from '../../utils/transforms.fields';
import { getDonationFormConfig } from '../../utils/transforms.donation_form';
import { ENVIRONMENT, DONATION_TYPES_PATH_ALIAS, RG } from '../../utils/constants';
import { getAttribution } from '../../utils/attribution';
import LandingPageTemplate from '../../components/04_templates/PageLayouts/LandingPage';
import Paragraphs from '../../components/02_molecules/Paragraphs';
import OrderEmailContent from '../../components/02_molecules/OrderEmailContent';
import DonationFormPage from '../../components/03_organisms/DonationFormPage';
import SourceCodeBehavior from '../../components/01_atoms/SourceCodeBehavior';
import CharityDonationSendGTMPurchase from '../../components/01_atoms/CharityDonationSendGTMPurchase';

import '../../styles/styles.scss';

/**
 * Appeal page has 3 modes:
 * - default (default body blocks)
 * - Thank You page (body blocks)
 * - Donation form
 *
 * All three modes use the same data (appeal entity). Navigation between
 * all modes handled using shallow routing meaning no data reload.
 */

const AppealPage = ({
  entity = null,
  blocks = [],
  thankYouBlocks = [],
  thankYouBlocksRG = [],
  isThankYouPage = false,
  originalUrl,
  router,
  layout = 'centered',
  nonce,
}) => {
  const donationFormConfig = getDonationFormConfig(entity);
  const url = donationFormConfig.appealUrl;
  // Get everything after appeal path alias.
  // We can't use currentPath from props since it causes redundant
  // rendering and may lead to unexpected consequences like
  // false redirects and wrong browser history.
  const pathSuffix =
    typeof window !== 'undefined' ? router.asPath.replace(url, '') : originalUrl.replace(url, '');

  if (isThankYouPage) {
    let thankYouPageBlocks = thankYouBlocks;
    // route.query.type will be different in case of client route or server request.
    // Client route: single_donation / recurring donation.
    // In case when 'Thank you' page was reloaded: single / monthly.
    if (
      router.query &&
      thankYouBlocksRG &&
      thankYouBlocksRG.length &&
      (router.query.type === RG || router.query.type === DONATION_TYPES_PATH_ALIAS[RG])
    ) {
      thankYouPageBlocks = thankYouBlocksRG;
    }

    // Handling source code behavior. The "Thank you" page is an exception to
    // the common rule, since it doesn't contain campaign ID in URL, we getting
    // it from session storage.
    // Also making sure it is the target Appeal by checking its ID.
    let sourceCode;
    const appealId = field.getNumberValue(entity, 'nid');
    const attribution = getAttribution();
    if (attribution && attribution.appeal_id && attribution.appeal_id === appealId) {
      sourceCode = attribution.source_code;
    }

    const isParallaxEffectEnabled = thankYouPageBlocks.some(
      (block) => block.blockType === 'parallax_effect',
    );

    return (
      <LandingPageTemplate
        data-component="thank-you-page"
        isParallaxEffectEnabled={isParallaxEffectEnabled}
      >
        <Head>
          <title>{field.getTextValue(entity, 'field_thankyou_page_title')}</title>
        </Head>
        {sourceCode && <SourceCodeBehavior sourceCode={sourceCode} />}
        <Paragraphs blocks={thankYouPageBlocks} />
        <CharityDonationSendGTMPurchase isThankYouPage={isThankYouPage} />
        {/*
          For all development envs we display Thank You email content
          on Thank You page for automated tests.
        */}
        {ENVIRONMENT !== 'production' && <OrderEmailContent />}
      </LandingPageTemplate>
    );
  }

  // Case for other checkout steps.
  const isCheckout = pathSuffix.startsWith('/checkout');

  if (isCheckout) {
    // TODO: use field transformers.
    const pageTitle =
      entity.field_meta_tags &&
      entity.field_meta_tags[0] &&
      entity.field_meta_tags[0].attributes.content
        ? entity.field_meta_tags[0].attributes.content
        : field.getTextValue(entity, 'title');

    return (
      <DonationFormPage
        formConfig={donationFormConfig}
        pageTitle={pageTitle}
        pathPrefix={url}
        pathSuffix={pathSuffix}
        nonce={nonce}
      />
    );
  }

  // Add Social Media share sidebar, but first make sure it's not been added yet on a previous
  // re-render, e.g. when the page is resized.
  const hasSocialMediaShareBlock =
    blocks.filter((block) => block.blockType === 'social_media_share').length > 0;

  if (!hasSocialMediaShareBlock) {
    blocks.push({
      blockType: 'social_media_share',
      activeProviders: ['facebook', 'twitter', 'linkedin', 'link'],
      hasMobileBorders: true,
      isDesktopSticky: true,
    });
  }

  return (
    <LandingPageTemplate>
      <Paragraphs blocks={blocks} layout={layout} />
    </LandingPageTemplate>
  );
};

AppealPage.getInitialProps = async ({ entity, originalUrl, res, pathname, query, router }) => {
  const initialProps = {};
  // Show error page if appeal page didn't get an appeal entity.
  if (!entity) {
    return { statusCode: 502 };
  }

  // There are some cases when appeal landing page should redirect straight to
  // checkout. To perform universal redirect (server & frontend) we use
  // getInitialProps.
  const newRoute = { pathname, query };
  const newAsPath = `${field.getEntityURL(entity).url}/checkout`;

  // Looks like we are already on checkout flow.
  if (originalUrl.startsWith(newAsPath)) {
    return initialProps;
  }

  const isEmptyPage = field.getArrayValue(entity, 'field_blocks').length === 0;
  const isCheckoutOnly = field.getBooleanValue(entity, 'field_is_checkout_only');

  if (isEmptyPage || isCheckoutOnly) {
    if (!res) {
      // Frontend.
      // TODO: Probably query should be kept for the client routing since we
      //   have it for the server. The proper way to achieve it at this step
      //   is not clear, and currently, there is no such a use case,
      //   so skipping it so far.
      router.push(newRoute, newAsPath, { shallow: true });
      return initialProps;
    }
    // Server-side, without caching.
    // In general, this kind of redirects is an edge case. If it's unwanted
    // behaviour, then 301 redirect make it harder for editors to fix & test the page.
    // Keeping the query if presented, to respect attribution.
    res.redirect(302, `${newAsPath}${query ? `?${toQuerystring(query)}` : ''}`);
  }

  return initialProps;
};

AppealPage.propTypes = {
  entity: PropTypes.shape({
    field_meta_tags: PropTypes.arrayOf({
      attributes: PropTypes.shape({
        content: PropTypes.string,
      }),
    }),
    url: PropTypes.shape({
      url: PropTypes.string,
      is_external: PropTypes.bool,
      entity_type: PropTypes.string,
      entity_bundle: PropTypes.string,
    }),
  }),
  blocks: PropTypes.arrayOf(
    PropTypes.shape({
      blockType: PropTypes.string.isRequired,
    }),
  ),
  thankYouBlocks: PropTypes.arrayOf(
    PropTypes.shape({
      blockType: PropTypes.string.isRequired,
    }),
  ),
  thankYouBlocksRG: PropTypes.arrayOf(
    PropTypes.shape({
      blockType: PropTypes.string.isRequired,
    }),
  ),

  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      nextLink: PropTypes.shape({
        href: PropTypes.string,
        as: PropTypes.string,
      }),
    }),
  ),
  router: PropTypes.shape().isRequired,
  originalUrl: PropTypes.string.isRequired,
  isThankYouPage: PropTypes.bool,
  layout: PropTypes.string,
  nonce: PropTypes.string,
};

export default withRouter(AppealPage);
